import React, { Component } from 'react'
import { Button, Input, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap'
import { SettingsIcon } from 'components/icons/settings'
import * as Flash from 'components/flash'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { Roles } from 'shared/constants'
import { Dataset } from 'generic/dataset'
import * as GenericRedux from '../../../generic/genericRedux'
import * as Constants from './constants'
import { 
  CommunityConfigurationProps, 
  CommunityConfigurationState, 
  CommunityConfiguration, 
  EmptyDisplayCommunityConfiguration,
  NewsletterCategory,
} from './types'
import './styles.css'

class CommunityConfigurationComponent extends Component<CommunityConfigurationProps, CommunityConfigurationState> {
  public static contextType = RasaContext
  private sharedStore: SharedStore

  constructor(props: CommunityConfigurationProps) {
    super(props)
    this.state = {
      communityId: 0,
      communityIdentifier: '',
      allConfigurations: [],
      defaultConfigurations: [],
      communityConfigurations: [],
      segmentConfigurations: {},
      segmentExpanded: {},
      newsletterCategories: [],
      isLoading: false,
      error: '',
      isSuperUser: false,
      defaultSectionExpanded: false,
      communitySectionExpanded: true,
      newsletterSectionExpanded: false,
      editingConfigId: null,
      editingValue: '',
      editingNewsletterCell: null,
      editingNewsletterValue: '',
      modalFormData: {
        config_key: '',
        config_value: '',
        config_type: Constants.CONFIG_TYPE_COMMUNITY,
      },
    }
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    this.initialize()
  }

  public render() {
    return (
      <div className="community-configuration-wrapper">
        <div className="contentpane-header-wrapper">
          <div className="contentpane-icon-wrapper">
            <SettingsIcon width={24} height={24} />
          </div>
          <div className="header">
            <div className="header-outter-wrapper">
              <div className="contentpane-section-title-wrapper">
                <h6 className="section-header-title">{Constants.COMMUNITY_CONFIG_FEATURE_TITLE}</h6>
                <div className="section-header-text">{Constants.COMMUNITY_CONFIG_FEATURE_DESCRIPTION}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="configuration-content">
          {this.state.isSuperUser && (
            <div className="add-config-section">
              <Button 
                className="add-config-button" 
                color="primary"
                onClick={this.openAddConfigModal}
              >
                {Constants.ADD_CONFIG_BUTTON_TEXT}
              </Button>
            </div>
          )}

          {this.state.isLoading ? (
            <div className="loading-container">
              <Loading size="32" />
              <div>{Constants.LOADING_CONFIGURATIONS_MESSAGE}</div>
            </div>
          ) : (
            <div className="configurations-sections">
              {/* Community Configurations Section - Show First */}
              <div className="configuration-section">
                <div 
                  className="section-header"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.toggleSection('community')
                  }}
                >
                  <div className="section-title">
                    <i className={`fa ${this.state.communitySectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true"></i>
                    <span>{Constants.COMMUNITY_CONFIGURATIONS_TITLE}</span>
                    <span className="config-count">({this.state.communityConfigurations.length})</span>
                  </div>
                </div>
                {this.state.communitySectionExpanded && (
                  <div className="section-content">
                    {this.renderConfigurationTable(this.state.communityConfigurations, Constants.NO_COMMUNITY_CONFIGURATIONS_MESSAGE, true)}
                    {this.renderSegmentSections()}
                  </div>
                )}
              </div>

              {/* Default Configurations Section - Show Second, Read-only */}
              <div className="configuration-section">
                <div 
                  className="section-header"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.toggleSection('default')
                  }}
                >
                  <div className="section-title">
                    <i className={`fa ${this.state.defaultSectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true"></i>
                    <span>{Constants.DEFAULT_CONFIGURATIONS_TITLE}</span>
                    <span className="config-count">({this.state.defaultConfigurations.length})</span>
                  </div>
                </div>
                {this.state.defaultSectionExpanded && (
                  <div className="section-content">
                    {this.renderConfigurationTable(this.state.defaultConfigurations, Constants.NO_DEFAULT_CONFIGURATIONS_MESSAGE, false)}
                  </div>
                )}
              </div>

              {/* Newsletter Counts Section - Show Third */}
              <div className="configuration-section">
                <div 
                  className="section-header"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.toggleSection('newsletter')
                  }}
                >
                  <div className="section-title">
                    <i className={`fa ${this.state.newsletterSectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true"></i>
                    <span>{Constants.NEWSLETTER_COUNTS_TITLE}</span>
                    <span className="config-count">({this.state.newsletterCategories.length})</span>
                  </div>
                </div>
                {this.state.newsletterSectionExpanded && (
                  <div className="section-content">
                    {this.renderNewsletterCountsTable()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {this.renderAddConfigModal()}
      </div>
    )
  }

  private initialize = () => {
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.role),
    ])
    .then(([activeCommunity, role]) => {
      this.setState({
        communityId: activeCommunity.data.community_id,
        communityIdentifier: activeCommunity.communityId,
        isSuperUser: role === Roles.super_admin,
      })
      this.loadConfigurations(activeCommunity.communityId)
    })
    .catch((error) => {
      this.setState({ error: 'Failed to initialize component' })
    })
  }

  private loadConfigurations = (communityIdentifier: string, forceRefresh: boolean = false) => {
    this.setState({ isLoading: true, error: '' })
    
    const dataset = new Dataset()
    const params = forceRefresh ? [{ param: 'forceRefresh', value: true }] : []
    
    // Load community configurations and newsletter categories
    Promise.all([
      dataset.loadCommunityDataset('communityConfiguration', communityIdentifier, params),
      dataset.loadCommunityDataset('newsletterCategories', communityIdentifier, params)
    ])
    .then(([configResponse, newsletterResponse]) => {
      let allConfigs: CommunityConfiguration[] = []
      
      // Process configurations
      if (configResponse && configResponse[0]) {
        allConfigs = configResponse[0]
        this.setState({ 
          allConfigurations: allConfigs,
          defaultConfigurations: this.filterConfigurations(allConfigs, Constants.CONFIG_SOURCE_DEFAULT),
          communityConfigurations: this.filterNonPartnerConfigurations(allConfigs, Constants.CONFIG_SOURCE_SPECIFIC),
        })
      } else {
        this.setState({ 
          allConfigurations: [],
          defaultConfigurations: [],
          communityConfigurations: [],
        })
      }

      // Process partner configurations and group by partner code
      const partnerConfigs = allConfigs.filter(config => 
        config.configuration_source === Constants.CONFIG_SOURCE_SPECIFIC && 
        config.community_partner_id && 
        config.community_partner_code
      )

      if (partnerConfigs.length > 0) {
        const segmentConfigs: Record<string, CommunityConfiguration[]> = {}
        const segmentExpandState: Record<string, boolean> = {}

        // Group configurations by community_partner_code
        partnerConfigs.forEach(config => {
          const partnerCode = config.community_partner_code
          if (partnerCode && !segmentConfigs[partnerCode]) {
            segmentConfigs[partnerCode] = []
            segmentExpandState[partnerCode] = false // Initially collapsed
          }
          if (partnerCode) {
            segmentConfigs[partnerCode].push(config)
          }
        })

        this.setState({
          segmentConfigurations: segmentConfigs,
          segmentExpanded: segmentExpandState
        })
      } else {
        this.setState({
          segmentConfigurations: {},
          segmentExpanded: {}
        })
      }

      // Process newsletter categories and populate with existing config values
      if (newsletterResponse && newsletterResponse[0]) {
        const configMap = allConfigs
          .filter(config => config.community_partner_id === null || config.community_partner_id === undefined)
          .reduce((acc, config) => {
            const key = config.config_key || config.key || ''
            const value = config.config_value || config.value || ''
            if (key) acc[key] = value
            return acc
          }, {} as Record<string, string>)

        const categories = newsletterResponse[0].map((category: NewsletterCategory) => {
          const categoryId = category.id
          const categoryName = category.name.toLowerCase()
          
          // Use correct key naming based on category_type id
          let maximumKey: string
          let minimumKey: string
          let maxPreferredKey: string
          let maxLeadKey: string
          let maxPerSourceKey: string
          
          if (categoryId === 1) {
            maximumKey = 'newsbrief_count'
            minimumKey = 'minimum_newsbrief_count'
            maxPreferredKey = 'max_preferred_count'
            maxLeadKey = 'max_lead_count'
            maxPerSourceKey = 'max_per_source'
          } else {
            maximumKey = `newsbrief_count_${categoryName}`
            minimumKey = `minimum_newsbrief_count_${categoryName}`
            maxPreferredKey = `max_preferred_count_${categoryName}`
            maxLeadKey = `max_lead_count_${categoryName}`
            maxPerSourceKey = `max_per_source_${categoryName}`
          }
          
          const mappedCategory = {
            ...category,
            maximum: configMap[maximumKey] || '',
            minimum: configMap[minimumKey] || '',
            max_preferred: configMap[maxPreferredKey] || '',
            max_lead: configMap[maxLeadKey] || '',
            max_per_source: configMap[maxPerSourceKey] || '',
          }
          
          return mappedCategory
        })
        
        this.setState({ newsletterCategories: categories })
      } else {
        this.setState({ newsletterCategories: [] })
      }

      this.setState({ isLoading: false })
    })
    .catch((error) => {
      this.setState({ 
        error: 'Failed to load configurations',
        isLoading: false,
      })
      this.context.store.dispatch(Flash.showFlashError(Constants.COMMUNITY_CONFIG_FAILURE_MESSAGE))
    })
  }

  private filterConfigurations = (configurations: CommunityConfiguration[], source: string): CommunityConfiguration[] => {
    return configurations.filter(config => config.configuration_source === source)
  }

  private filterNonPartnerConfigurations = (configurations: CommunityConfiguration[], source: string): CommunityConfiguration[] => {
    return configurations.filter(config => 
      config.configuration_source === source && 
      (config.community_partner_id === null || config.community_partner_id === undefined)
    )
  }

  private toggleSection = (sectionType: 'default' | 'community' | 'newsletter') => {
    if (sectionType === 'default') {
      this.setState({ defaultSectionExpanded: !this.state.defaultSectionExpanded })
    } else if (sectionType === 'community') {
      this.setState({ communitySectionExpanded: !this.state.communitySectionExpanded })
    } else {
      this.setState({ newsletterSectionExpanded: !this.state.newsletterSectionExpanded })
    }
  }

  private toggleSegmentSection = (partnerCode: string) => {
    this.setState({
      segmentExpanded: {
        ...this.state.segmentExpanded,
        [partnerCode]: !this.state.segmentExpanded[partnerCode]
      }
    })
  }

  private renderConfigurationTable = (configurations: CommunityConfiguration[], emptyMessage: string, allowEditing: boolean = true) => {
    if (configurations.length === 0) {
      return (
        <div className="no-configurations">
          {emptyMessage}
        </div>
      )
    }

    return (
      <Table responsive striped>
        <thead>
          <tr>
            <th>{Constants.COLUMN_KEY}</th>
            <th>{Constants.COLUMN_VALUE}</th>
            {allowEditing && <th>{Constants.COLUMN_ACTION}</th>}
          </tr>
        </thead>
        <tbody>
          {configurations.map((config) => this.renderConfigurationRow(config, allowEditing))}
        </tbody>
      </Table>
    )
  }

  private renderConfigurationRow = (config: CommunityConfiguration, allowEditing: boolean = true) => {
    const isEditing = this.state.editingConfigId === config.id
    const displayValue = isEditing ? this.state.editingValue : (config.value || config.config_value || '')
    const configKey = config.key || config.config_key || ''
    const isImageUrl = configKey && configKey.toLowerCase().endsWith('image_url')
    
    return (
      <tr key={config.id}>
        <td className="config-key-cell">{configKey || 'Unknown Key'}</td>
        <td className="config-value-cell">
          {isEditing ? (
            <Input
              type="text"
              value={displayValue}
              onChange={(e) => this.setState({ editingValue: e.target.value })}
              placeholder={Constants.CONFIG_VALUE_PLACEHOLDER}
              autoFocus
            />
          ) : (
            this.renderConfigValue(config, isImageUrl)
          )}
        </td>
        {allowEditing && (
          <td className="config-actions-cell">
            {this.renderActionButtons(config, isEditing, allowEditing)}
          </td>
        )}
      </tr>
    )
  }

  private renderConfigValue = (config: CommunityConfiguration, isImageUrl: boolean) => {
    const value = config && (config.value || config.config_value) ? (config.value || config.config_value) : ''
    const configKey = config.key || config.config_key || ''
    
    if (isImageUrl && value) {
      return (
        <div className="config-image-container">
          <img
            src={value}
            alt={configKey || 'Configuration image'}
            title={`Image URL: ${value}`}
            className="config-image-preview"
            onError={(e) => {
              // Show URL text if image fails to load
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              const fallback = target.nextElementSibling as HTMLElement
              if (fallback) {
                fallback.style.display = 'inline-block'
              }
            }}
          />
          <span className="config-image-fallback" style={{display: 'none'}}>
            <span className="config-value-display">{value}</span>
          </span>
          <div className="config-image-info">
            <small className="text-muted">Click edit to modify URL</small>
          </div>
        </div>
      )
    }
    
    return <span className="config-value-display">{value}</span>
  }

  private renderActionButtons = (config: CommunityConfiguration, isEditing: boolean, allowEditing: boolean = true) => {
    if (!this.state.isSuperUser || !allowEditing) {
      return <span className="no-actions">-</span>
    }

    if (isEditing) {
      return (
        <div className="action-icons">
          <i 
            className="fa fa-check action-icon save-icon" 
            aria-hidden="true"
            onClick={() => this.saveConfiguration(config)}
            title="Save changes"
          ></i>
          <i 
            className="fa fa-times action-icon cancel-icon" 
            aria-hidden="true"
            onClick={this.cancelEditing}
            title="Cancel editing"
          ></i>
        </div>
      )
    }

    return (
      <div className="action-icons">
        <i 
          className="fa fa-pencil action-icon edit-icon" 
          aria-hidden="true"
          onClick={() => this.startEditing(config)}
          title="Edit configuration"
        ></i>
        <i 
          className="fa fa-trash action-icon delete-icon" 
          aria-hidden="true"
          onClick={() => this.deleteConfiguration(config)}
          title="Delete configuration"
        ></i>
      </div>
    )
  }

  private startEditing = (config: CommunityConfiguration) => {
    this.setState({
      editingConfigId: config.id,
      editingValue: config.value || config.config_value || ''
    })
  }

  private cancelEditing = () => {
    this.setState({
      editingConfigId: null,
      editingValue: ''
    })
  }

  private saveConfiguration = (config: CommunityConfiguration) => {
    const configKey = config.key || config.config_key || ''
    const configValue = this.state.editingValue
    
    this.context.entityMetadata.getEntityObject('community_configuration')
      .then((entity) => {
        entity.setRecordId(null, null)
        entity.data.community_id = config.community_id
        entity.data.key = configKey
        entity.data.value = configValue
        entity.data.community_partner_id = config.community_partner_id
        entity.data.is_active = true
        return entity.save()
      })
      .then(() => {
        // Update local state for immediate UI feedback
        this.updateLocalConfiguration(config.id, configValue)
        this.setState({
          editingConfigId: null,
          editingValue: ''
        })
        
        // Force refresh to ensure cache is cleared
        this.loadConfigurations(this.state.communityIdentifier, true)
        
        this.context.store.dispatch(Flash.showFlashMessage(Constants.COMMUNITY_CONFIG_SUCCESS_MESSAGE))
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error saving configuration:', error.message)
        this.context.store.dispatch(Flash.showFlashError(Constants.COMMUNITY_CONFIG_FAILURE_MESSAGE))
      })
  }

  private updateLocalConfiguration = (configId: number, newValue: string) => {
    const updateArray = (configs: CommunityConfiguration[]) => 
      configs.map(c => c.id === configId ? { 
        ...c, 
        value: newValue,
        config_value: newValue 
      } : c)

    this.setState({
      allConfigurations: updateArray(this.state.allConfigurations),
      defaultConfigurations: updateArray(this.state.defaultConfigurations),
      communityConfigurations: updateArray(this.state.communityConfigurations)
    })
  }

  private renderNewsletterCountsTable = () => {
    if (this.state.newsletterCategories.length === 0) {
      return (
        <div className="no-configurations">
          {Constants.NO_NEWSLETTER_CATEGORIES_MESSAGE}
        </div>
      )
    }

    return (
      <Table responsive className="newsletter-counts-table">
        <thead>
          <tr>
            <th>{Constants.NEWSLETTER_COLUMN_NAME}</th>
            <th>{Constants.NEWSLETTER_COLUMN_MAXIMUM}</th>
            <th>{Constants.NEWSLETTER_COLUMN_MINIMUM}</th>
            <th>{Constants.NEWSLETTER_COLUMN_MAX_PREFERRED}</th>
            <th>{Constants.NEWSLETTER_COLUMN_MAX_LEAD}</th>
            <th>{Constants.NEWSLETTER_COLUMN_MAX_PER_SOURCE}</th>
            {this.state.isSuperUser && <th>{Constants.COLUMN_ACTION}</th>}
          </tr>
        </thead>
        <tbody>
          {this.state.newsletterCategories.map((category) => this.renderNewsletterCategoryRow(category))}
        </tbody>
      </Table>
    )
  }

  private renderSegmentSections = () => {
    if (Object.keys(this.state.segmentConfigurations).length === 0) {
      return null
    }

    return (
      <div className="segment-sections">
        {Object.keys(this.state.segmentConfigurations).map(partnerCode => {
          const configurations = this.state.segmentConfigurations[partnerCode]
          const isExpanded = this.state.segmentExpanded[partnerCode]
          
          if (!configurations || configurations.length === 0) {
            return null
          }

          return (
            <div key={partnerCode} className="segment-section">
              <div 
                className="segment-header"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  this.toggleSegmentSection(partnerCode)
                }}
              >
                <div className="segment-title">
                  <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true"></i>
                  <span>{partnerCode}</span>
                  <span className="config-count">({configurations.length})</span>
                </div>
              </div>
              {isExpanded && (
                <div className="segment-content">
                  {this.renderConfigurationTable(configurations, `No configurations found for ${partnerCode}`, true)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  private renderNewsletterCategoryRow = (category: NewsletterCategory) => {
    const isEditingRow = this.state.editingNewsletterCell && this.state.editingNewsletterCell.categoryId === category.id
    const fields = ['maximum', 'minimum', 'max_preferred', 'max_lead', 'max_per_source']

    return (
      <tr key={category.id}>
        <td className="newsletter-category-name">{category.display_name}</td>
        {fields.map((field) => {
          const value = category[field as keyof NewsletterCategory] as string || ''
          
          return (
            <td key={field} className="newsletter-count-cell">
              {isEditingRow ? (
                <Input
                  type="number"
                  value={category[field as keyof NewsletterCategory] as string || '0'}
                  onChange={(e) => this.updateNewsletterCategoryField(category.id, field, e.target.value)}
                  className="newsletter-input"
                />
              ) : (
                <div className="newsletter-count-value">
                  {value || '0'}
                </div>
              )}
            </td>
          )
        })}
        {this.state.isSuperUser && (
          <td className="newsletter-actions-cell">
            {isEditingRow ? (
              <div className="newsletter-action-buttons">
                <i 
                  className="fa fa-check action-icon save-icon" 
                  onClick={() => this.saveNewsletterCategory(category)}
                  title="Save changes"
                ></i>
                <i 
                  className="fa fa-times action-icon cancel-icon" 
                  onClick={() => this.cancelNewsletterEdit()}
                  title="Cancel editing"
                ></i>
              </div>
            ) : (
              <div className="newsletter-action-buttons">
                <i 
                  className="fa fa-pencil action-icon edit-icon" 
                  onClick={() => this.startNewsletterRowEdit(category)}
                  title="Edit row"
                ></i>
              </div>
            )}
          </td>
        )}
      </tr>
    )
  }

  private startNewsletterRowEdit = (category: NewsletterCategory) => {
    if (!this.state.isSuperUser) return
    
    this.setState({
      editingNewsletterCell: { categoryId: category.id, field: 'row' }
    })
  }

  private cancelNewsletterEdit = () => {
    // Reload data to revert any unsaved changes
    this.loadConfigurations(this.state.communityIdentifier)
    this.setState({
      editingNewsletterCell: null
    })
  }

  private updateNewsletterCategoryField = (categoryId: number, field: string, value: string) => {
    const updatedCategories = this.state.newsletterCategories.map(cat => 
      cat.id === categoryId 
        ? { ...cat, [field]: value }
        : cat
    )
    this.setState({ newsletterCategories: updatedCategories })
  }

  private getConfigKey = (field: string, category: NewsletterCategory): string => {
    const categoryId = category.id
    const categoryName = category.name.toLowerCase()
    
    // For category_type id = 1, use base keys
    if (categoryId === 1) {
      switch (field) {
        case 'maximum': return 'newsbrief_count'
        case 'minimum': return 'minimum_newsbrief_count'
        case 'max_preferred': return 'max_preferred_count'
        case 'max_lead': return 'max_lead_count'
        case 'max_per_source': return 'max_per_source'
        default: return field
      }
    } else {
      // For category_type id > 1, append category name
      switch (field) {
        case 'maximum': return `newsbrief_count_${categoryName}`
        case 'minimum': return `minimum_newsbrief_count_${categoryName}`
        case 'max_preferred': return `max_preferred_count_${categoryName}`
        case 'max_lead': return `max_lead_count_${categoryName}`
        case 'max_per_source': return `max_per_source_${categoryName}`
        default: return `${field}_${categoryName}`
      }
    }
  }

  private saveNewsletterCategory = (category: NewsletterCategory) => {
    const fields = ['maximum', 'minimum', 'max_preferred', 'max_lead', 'max_per_source']
    const promises = fields.map(field => {
      const configKey = this.getConfigKey(field, category)
      const value = category[field as keyof NewsletterCategory] as string || '0'
      
      return this.context.entityMetadata.getEntityObject('community_configuration')
        .then((entity) => {
          entity.setRecordId(null, null)
          entity.data.community_id = this.state.communityId
          entity.data.key = configKey
          entity.data.value = value
          entity.data.community_partner_id = null
          entity.data.is_active = true
          return entity.save()
        })
    })

    Promise.all(promises)
      .then(() => {
        // Clear editing state first
        this.setState({
          editingNewsletterCell: null
        })
        
        // Force refresh data to clear cache and get updated values
        this.loadConfigurations(this.state.communityIdentifier, true)
        
        this.context.store.dispatch(Flash.showFlashMessage(Constants.NEWSLETTER_COUNT_SUCCESS_MESSAGE))
      })
      .catch((error) => {
        this.setState({
          editingNewsletterCell: null
        })
        this.context.store.dispatch(Flash.showFlashError(Constants.NEWSLETTER_COUNT_FAILURE_MESSAGE))
      })
  }

  private deleteConfiguration = (config: CommunityConfiguration) => {
    const configKey = config.key || config.config_key || 'Unknown'
    if (!window.confirm(`${Constants.CONFIRM_DELETE_MESSAGE}\n\nConfiguration: ${configKey}`)) {
      return
    }

    this.context.entityMetadata.getEntityObject('communityConfiguration')
      .then((entity) => {
        entity.setRecordId(config.id, null)
        return entity.delete()
      })
      .then(() => {
        // Remove from local state
        this.removeLocalConfiguration(config.id)
        this.context.store.dispatch(Flash.showFlashMessage(Constants.COMMUNITY_CONFIG_DELETE_SUCCESS_MESSAGE))
      })
      .catch(() => {
        this.context.store.dispatch(Flash.showFlashError(Constants.COMMUNITY_CONFIG_DELETE_FAILURE_MESSAGE))
      })
  }

  private removeLocalConfiguration = (configId: number) => {
    const filterArray = (configs: CommunityConfiguration[]) => 
      configs.filter(c => c.id !== configId)

    this.setState({
      allConfigurations: filterArray(this.state.allConfigurations),
      defaultConfigurations: filterArray(this.state.defaultConfigurations),
      communityConfigurations: filterArray(this.state.communityConfigurations)
    })
  }


  private openAddConfigModal = () => {
    this.setState({
      modalFormData: {
        config_key: '',
        config_value: '',
        config_type: Constants.CONFIG_TYPE_COMMUNITY,
      },
    })
    this.props.openModal('AddConfigModal', {})
  }

  private renderAddConfigModal = () => {
    const modalData = this.props.modals.AddConfigModal
    if (!modalData || !modalData.isOpen) {
      return null
    }

    return (
      <Modal isOpen={true} size="lg">
        <ModalHeader>{Constants.ADD_CONFIG_MODAL_TITLE}</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>{Constants.CONFIG_TYPE_DROPDOWN_LABEL}</Label>
              <Input
                type="select"
                value={this.state.modalFormData.config_type}
                onChange={(e) => this.updateModalField('config_type', e.target.value)}
              >
                <option value={Constants.CONFIG_TYPE_COMMUNITY}>{Constants.CONFIG_TYPE_COMMUNITY}</option>
                <option value={Constants.CONFIG_TYPE_DEFAULT}>{Constants.CONFIG_TYPE_DEFAULT}</option>
              </Input>
            </FormGroup>
            
            <FormGroup>
              <Label>Configuration Key</Label>
              <Input
                type="text"
                value={this.state.modalFormData.config_key}
                placeholder={Constants.CONFIG_KEY_PLACEHOLDER}
                onChange={(e) => this.updateModalField('config_key', e.target.value)}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Configuration Value</Label>
              <Input
                type="text"
                value={this.state.modalFormData.config_value}
                placeholder={Constants.CONFIG_VALUE_PLACEHOLDER}
                onChange={(e) => this.updateModalField('config_value', e.target.value)}
              />
            </FormGroup>
            
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.saveNewConfiguration}>
            {Constants.SAVE_CONFIG_BUTTON_TEXT}
          </Button>
          <Button color="secondary" onClick={() => this.props.closeModal('AddConfigModal')}>
            {Constants.CANCEL_BUTTON_TEXT}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private updateModalField = (fieldName: string, fieldValue: string) => {
    this.setState({
      modalFormData: {
        ...this.state.modalFormData,
        [fieldName]: fieldValue,
      }
    })
  }

  private saveNewConfiguration = () => {
    const { config_key, config_value, config_type } = this.state.modalFormData

    if (!config_key || !config_value) {
      this.context.store.dispatch(Flash.showFlashError('Please fill in all required fields'))
      return
    }

    // Determine community_id: -1 for default configs, current community for community configs
    const communityId = config_type === Constants.CONFIG_TYPE_DEFAULT ? -1 : this.state.communityId

    this.context.entityMetadata.getEntityObject('community_configuration')
      .then((entity) => {
        entity.setRecordId(null, null)
        entity.data.community_id = communityId
        entity.data.key = config_key
        entity.data.value = config_value
        entity.data.community_partner_id = null
        entity.data.is_active = true
        return entity.save()
      })
      .then(() => {
        this.handleSuccessfulSave()
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error creating configuration:', err.message)
        this.context.store.dispatch(Flash.showFlashError(Constants.COMMUNITY_CONFIG_CREATE_FAILURE_MESSAGE))
      })
  }

  private handleSuccessfulSave = () => {
    this.props.closeModal('AddConfigModal')
    this.setState({
      modalFormData: {
        config_key: '',
        config_value: '',
        config_type: Constants.CONFIG_TYPE_COMMUNITY,
      }
    })
    this.loadConfigurations(this.state.communityIdentifier, true)
    this.context.store.dispatch(Flash.showFlashMessage(Constants.COMMUNITY_CONFIG_CREATE_SUCCESS_MESSAGE))
  }
}

export const CommunityConfigurationFeature = GenericRedux.registerNewComponentWithModals<CommunityConfiguration>(
  CommunityConfigurationComponent,
  'community_configuration',
  ['AddConfigModal'],
  EmptyDisplayCommunityConfiguration,
)